/**
 * @fileoverview Paint window between choosing an option and leaving the screen.
 * @module screen-view-v2/hooks/useDeferredAdvance
 *
 * D-T9b-17 (engine half). A single-pick screen used to call `onSelectionChange` and
 * `onAction` in the same tick: the engine swapped the screen inside the same commit, so
 * the selected state never reached a frame. Combined with the missing header Back (the
 * compile half, fixed in W2) a mis-tap on the 26-screen weight-loss route was permanently
 * uncorrectable — the user could not even tell WHICH answer had been recorded.
 *
 * The deferral is a paint window, not a confirmation step: one tap still answers the
 * question, and nothing waits on the user.
 */
import { useCallback, useEffect, useRef } from 'react';

/**
 * How long the chosen option stays on screen before the flow advances.
 *
 * The defect requires >= 150 ms. 180 ms buys ~2 frames of headroom at 60 Hz over the React
 * commit + paint that has to happen first, and stays well under the ~250 ms at which a
 * transition starts to feel like waiting.
 */
export const SELECTED_STATE_ADVANCE_DELAY_MS = 180;

export interface DeferredAdvance {
  /** Schedule `action`; a pending one is replaced, so a double tap advances once. */
  schedule: (action: () => void) => void;
  /** Drop a pending advance without running it. */
  cancel: () => void;
}

export function useDeferredAdvance(delayMs: number = SELECTED_STATE_ADVANCE_DELAY_MS): DeferredAdvance {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback((action: () => void) => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      action();
    }, delayMs);
  }, [cancel, delayMs]);

  // A screen that unmounts inside the window must not navigate afterwards — that would
  // fire an action against a step the engine has already left.
  // Not the only guard: the engine drops actions of a no-longer-current screen (exit layer, I-1).
  useEffect(() => cancel, [cancel]);

  return { schedule, cancel };
}
